/**
 * TenantLayoutContext — provides the active structural layout configuration
 * to all components in the tree.
 *
 * Layout is a layer on top of CSS variables: while colors, typography, and
 * spacing are applied as CSS custom properties on <body>, layout decisions
 * (hero section variant, product grid columns, category filter placement) drive
 * React component structure and cannot be expressed with CSS alone.
 *
 * Initialisation:
 *   On mount, reads the layout from the computed CSS vars on <body> (which
 *   reflect the static tenant CSS file values). This means the CSS file is the
 *   single source of truth at startup — no separate DB field is required.
 *
 * Runtime updates:
 *   When the admin selects a new preset in CmsManagerTheme, applyThemeVars()
 *   dispatches a THEME_LAYOUT_EVENT_NAME CustomEvent. The provider listens and
 *   updates its state, triggering a re-render in all consumers.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    type ThemeLayout,
    DEFAULT_LAYOUT,
    THEME_LAYOUT_EVENT_NAME,
    readLayoutFromBody,
} from './themes';

const TenantLayoutContext = createContext<ThemeLayout>(DEFAULT_LAYOUT);

export function TenantLayoutProvider({ children }: { children: React.ReactNode }) {
    const [layout, setLayout] = useState<ThemeLayout>(() => {
        if (typeof document === 'undefined') return DEFAULT_LAYOUT;
        return readLayoutFromBody();
    });

    useEffect(() => {
        const handler = (e: Event) =>
            setLayout((e as CustomEvent<ThemeLayout>).detail);
        window.addEventListener(THEME_LAYOUT_EVENT_NAME, handler);
        return () => window.removeEventListener(THEME_LAYOUT_EVENT_NAME, handler);
    }, []);

    return (
        <TenantLayoutContext.Provider value={layout}>
            {children}
        </TenantLayoutContext.Provider>
    );
}

/** Returns the current structural layout configuration for this tenant. */
export function useTenantLayout(): ThemeLayout {
    return useContext(TenantLayoutContext);
}
